/**
 * SlimHomeHeader — one-row sticky header for SocialPulse.com
 *
 * ONLY rendered on the home page (`/`) so we don't break the existing
 * multi-row Header used elsewhere.
 *
 * v4.2 (2026-02-19)
 *   • Adds an external "Blog" tab that opens https://socialpulse.blog
 *     in a new tab.
 *   • Adds hover/focus dropdowns on Explore, Places, Topics, Community,
 *     Causes, Network and About that jump to the correct in-page section
 *     (via # anchors) or to cross-site destinations across the network.
 *   • Mobile drawer expands each menu inline.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ChevronDown, Menu, X, ExternalLink } from "lucide-react";

/* -----------------------------------------------------------------
   Nav config — each entry is either:
     { label, to, blog: true }        → single external link
     { label, to }                    → single internal link
     { label, to?, items: [...] }     → dropdown; `to` (optional) makes
                                         the parent label a link too
   Dropdown items may point at:
     - "/#feed"           in-page section on home
     - "/explore"         internal route
     - { href, external:true }  cross-network domain
   -----------------------------------------------------------------*/
const NAV = [
  {
    label: "Explore",
    to: "/explore",
    items: [
      { label: "Highlights",        to: "/#highlights" },
      { label: "For You / Near You", to: "/#feed" },
      { label: "Local Pulse",       to: "/#local" },
      { label: "Popular Categories", to: "/#categories" },
      { label: "Events",            to: "/#events" },
      { label: "Full Explore →",    to: "/explore" },
    ],
  },
  {
    label: "Places",
    to: "/explore",
    items: [
      { label: "Places Directory",  to: "/explore" },
      { label: "Housing & Homes",   to: "/#housing" },
      { label: "Local Pulse Feed",  to: "/#local" },
      { label: "HomesPulse ↗",      href: "https://homespulse.com", external: true },
    ],
  },
  {
    label: "Topics",
    to: "/topics",
    items: [
      { label: "All Categories",    to: "/#categories" },
      { label: "Popular Topics",    to: "/topics" },
      { label: "Community Stories", to: "/#stories" },
      { label: "Causes",            to: "/causes" },
    ],
  },
  {
    label: "Community",
    to: "/community",
    items: [
      { label: "I Need Help / I Can Help", to: "/#help" },
      { label: "Community Feed",    to: "/#feed" },
      { label: "Organizations",     to: "/#organizations" },
      { label: "Events",            to: "/#events" },
      { label: "Stories",           to: "/#stories" },
    ],
  },
  {
    label: "Causes",
    to: "/causes",
    items: [
      { label: "All Causes",        to: "/causes" },
      { label: "Featured Causes",   to: "/#feed?tab=causes" },
      { label: "Volunteer / Help",  to: "/#help" },
    ],
  },
  {
    label: "Network",
    to: "/#network",
    items: [
      { label: "SocialPulse Blog ↗", href: "https://socialpulse.blog", external: true },
      { label: "FarmersPulse ↗",     href: "https://farmerspulse.com",  external: true },
      { label: "FoodPulse ↗",        href: "https://foodpulse.net",     external: true },
      { label: "NaturePulse ↗",      href: "https://naturepulse.net",   external: true },
      { label: "TravelerPulse ↗",    href: "https://travelerpulse.pro", external: true },
      { label: "AirlinesAirports ↗", href: "https://airlinesairports.com", external: true },
      { label: "AlumniPulse ↗",      href: "https://alumnipulse.net",   external: true },
      { label: "WeatherPulse ↗",     href: "https://weatherpulse.in",   external: true },
      { label: "IndianFamilyTree ↗", href: "https://indianfamilytree.com", external: true },
      { label: "FamilyTree.team ↗",  href: "https://familytree.team",   external: true },
      { label: "SpLink.pro ↗",       href: "https://splink.pro",        external: true },
      { label: "Full Network →",     to: "/#network" },
    ],
  },
  {
    label: "About",
    to: "/about",
    items: [
      { label: "About SocialPulse", to: "/about" },
      { label: "How It Works",      to: "/#changer" },
      { label: "Contact",           to: "/contact" },
    ],
  },
  // Direct external tab — no dropdown.
  { label: "Blog", href: "https://socialpulse.blog", external: true, blog: true },
];

/* -----------------------------------------------------------------
   Dropdown item renderer (desktop panel + mobile list)
   -----------------------------------------------------------------*/
function DropdownItem({ item, onClose, testid }) {
  const cls =
    "flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm text-[#0B1226] hover:bg-[#F0F5FA] transition-colors";
  if (item.external) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className={cls}
        onClick={onClose}
        data-testid={testid}
      >
        <span>{item.label}</span>
        <ExternalLink className="w-3.5 h-3.5 opacity-60" />
      </a>
    );
  }
  return (
    <Link to={item.to} className={cls} onClick={onClose} data-testid={testid}>
      {item.label}
    </Link>
  );
}

/* -----------------------------------------------------------------
   Desktop tab with hover / focus dropdown
   -----------------------------------------------------------------*/
function NavTab({ entry }) {
  const [open, setOpen] = useState(false);
  const testKey = entry.label.toLowerCase().replace(/\s+/g, "-");

  // Simple external tab (Blog)
  if (entry.external && !entry.items) {
    return (
      <a
        href={entry.href}
        target="_blank"
        rel="noopener noreferrer"
        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-1 ${
          entry.blog
            ? "text-[#7B4A9C] hover:bg-[#F5EBFA]"
            : "text-[#0B1226] hover:bg-[#F0F5FA]"
        }`}
        data-testid={`slim-nav-${testKey}`}
      >
        {entry.label}
        <ExternalLink className="w-3 h-3 opacity-60" />
      </a>
    );
  }

  // No dropdown → plain link
  if (!entry.items) {
    return (
      <Link
        to={entry.to}
        className="px-3 py-1.5 rounded-lg text-sm font-medium text-[#0B1226] hover:bg-[#F0F5FA] transition-colors"
        data-testid={`slim-nav-${testKey}`}
      >
        {entry.label}
      </Link>
    );
  }

  // With dropdown
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(e) => {
        // close only when focus leaves the whole group
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      <Link
        to={entry.to || "#"}
        className="px-3 py-1.5 rounded-lg text-sm font-medium text-[#0B1226] hover:bg-[#F0F5FA] transition-colors inline-flex items-center gap-1"
        aria-haspopup="true"
        aria-expanded={open}
        data-testid={`slim-nav-${testKey}`}
      >
        {entry.label}
        <ChevronDown
          className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </Link>
      {open && (
        <div
          className="absolute top-full left-0 pt-2 min-w-[240px] z-50"
          data-testid={`slim-nav-${testKey}-dropdown`}
        >
          <div className="bg-white rounded-xl shadow-xl ring-1 ring-[#E2E8F0] p-2">
            {entry.items.map((it, i) => (
              <DropdownItem
                key={i}
                item={it}
                onClose={() => setOpen(false)}
                testid={`slim-nav-${testKey}-item-${i}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* -----------------------------------------------------------------
   Main header component
   -----------------------------------------------------------------*/
export default function SlimHomeHeader() {
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [q, setQ] = useState("");

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header
      data-testid="slim-home-header"
      className="sticky top-0 z-50 bg-[#FDFBF7]/95 backdrop-blur-xl border-b border-[#E2E8F0]"
    >
      <div className="max-w-[1360px] mx-auto px-4 lg:px-8 h-[72px] flex items-center gap-6">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2 shrink-0"
          data-testid="slim-header-logo"
        >
          <span className="w-9 h-9 rounded-xl bg-[#7B4A9C] flex items-center justify-center shadow-sm">
            <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
            </svg>
          </span>
          <span className="font-bold text-[#0B1226] text-lg tracking-tight">
            SocialPulse<span className="text-[#7B4A9C]">®</span>
          </span>
        </Link>

        {/* Center nav (desktop) */}
        <nav
          className="hidden lg:flex items-center gap-1 flex-1"
          aria-label="Primary"
          data-testid="slim-header-nav"
        >
          {NAV.map((entry) => (
            <NavTab key={entry.label} entry={entry} />
          ))}
        </nav>
        <div className="flex-1 lg:hidden" />

        {/* Right cluster */}
        <div className="flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden md:block relative">
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search"
              aria-label="Search SocialPulse"
              className="w-40 lg:w-48 pl-8 pr-3 py-1.5 rounded-full text-sm bg-white border border-[#E2E8F0] focus:border-[#1E64C8] focus:ring-2 focus:ring-[#1E64C8]/25 outline-none"
              data-testid="slim-header-search-input"
            />
            <Search className="w-4 h-4 text-[#50586C] absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </form>
          <button
            type="button"
            aria-label="Search"
            className="md:hidden w-9 h-9 rounded-full hover:bg-[#F0F5FA] inline-flex items-center justify-center"
            onClick={() => nav("/search")}
          >
            <Search className="w-4 h-4 text-[#0B1226]" />
          </button>
          <button
            type="button"
            aria-label="Language"
            className="hidden sm:inline-flex items-center gap-0.5 text-sm font-medium text-[#0B1226] px-2 py-1 rounded-lg hover:bg-[#F0F5FA]"
            data-testid="slim-header-lang"
          >
            English <ChevronDown className="w-3 h-3" />
          </button>
          <Link
            to="/login"
            className="text-sm font-medium text-[#0B1226] px-3 py-1.5 rounded-lg hover:bg-[#F0F5FA] whitespace-nowrap"
            data-testid="slim-header-signin"
          >
            Sign In
          </Link>
          <Link
            to="/join"
            className="hidden sm:inline-flex items-center bg-[#1E64C8] hover:bg-[#164FA0] text-white text-sm font-semibold px-4 py-2 rounded-full shadow-sm transition-colors whitespace-nowrap"
            data-testid="slim-header-join"
          >
            Join SocialPulse
          </Link>
          <button
            type="button"
            className="lg:hidden w-9 h-9 rounded-full hover:bg-[#F0F5FA] inline-flex items-center justify-center"
            aria-label="Menu"
            onClick={() => setMobileOpen(true)}
            data-testid="slim-header-menu"
          >
            <Menu className="w-4 h-4 text-[#0B1226]" />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[60] bg-[#0B1226]/40"
          onClick={() => setMobileOpen(false)}
          data-testid="slim-header-mobile-drawer"
        >
          <div
            className="absolute top-0 right-0 h-full w-80 max-w-[90vw] bg-[#FDFBF7] shadow-xl p-5 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="font-bold text-[#0B1226]">Menu</span>
              <button onClick={() => setMobileOpen(false)} aria-label="Close">
                <X className="w-5 h-5 text-[#0B1226]" />
              </button>
            </div>
            <ul className="space-y-1">
              {NAV.map((entry) => {
                const testKey = entry.label.toLowerCase().replace(/\s+/g, "-");
                // External blog / no-dropdown external
                if (entry.external && !entry.items) {
                  return (
                    <li key={entry.label}>
                      <a
                        href={entry.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium text-[#0B1226] hover:bg-[#F0F5FA]"
                      >
                        <span>{entry.label}</span>
                        <ExternalLink className="w-4 h-4 opacity-60" />
                      </a>
                    </li>
                  );
                }
                // No dropdown
                if (!entry.items) {
                  return (
                    <li key={entry.label}>
                      <Link
                        to={entry.to}
                        onClick={() => setMobileOpen(false)}
                        className="block px-3 py-2.5 rounded-lg text-sm font-medium text-[#0B1226] hover:bg-[#F0F5FA]"
                      >
                        {entry.label}
                      </Link>
                    </li>
                  );
                }
                // With dropdown (collapsible)
                const isOpen = openGroup === entry.label;
                return (
                  <li key={entry.label}>
                    <button
                      type="button"
                      onClick={() =>
                        setOpenGroup(isOpen ? null : entry.label)
                      }
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium text-[#0B1226] hover:bg-[#F0F5FA]"
                      data-testid={`mobile-nav-${testKey}`}
                    >
                      <span>{entry.label}</span>
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {isOpen && (
                      <ul className="pl-3 border-l border-[#E2E8F0] ml-3 my-1 space-y-0.5">
                        {entry.items.map((it, i) => (
                          <li key={i}>
                            <DropdownItem
                              item={it}
                              onClose={() => setMobileOpen(false)}
                              testid={`mobile-nav-${testKey}-item-${i}`}
                            />
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="mt-5 pt-5 border-t border-[#E2E8F0] space-y-2">
              <Link
                to="/login"
                className="block w-full text-center py-2.5 rounded-full font-semibold text-[#0B1226] border border-[#E2E8F0]"
              >
                Sign In
              </Link>
              <Link
                to="/join"
                className="block w-full text-center py-2.5 rounded-full font-semibold text-white bg-[#1E64C8]"
              >
                Join SocialPulse
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
